import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
  type RouterScrollBehavior,
  type Router
} from 'vue-router'
import { TransactionFilters, TransactionsDetailsQuery } from '@models/entities/transactions'
import { useGlobalStore } from '@stores/globalStore'
import { useAuthorizationStore } from '@stores/authorizationStore'
import { useUserStore } from '@stores/userStore'
import { useLocalizationStore } from '@stores/localizationStore'
import { usePointsStore } from '@stores/pointsStore'

import { SettingsQuery } from '@models/entities/settings'

import commonRoutes from '@/router/commonRoutes'
import { NavigationRoutesNames } from '@definitions/navigation'

const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) =>
  to.path === from.path && savedPosition ? savedPosition : { left: 0, top: 0 }

export function getRoutesList(): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = [
    {
      name: 'home',
      path: '/',
      children: commonRoutes
    }
  ]

  routes.push({
    path: '/transactions',
    name: 'transactions-redirect',
    redirect: (to) => {
      return {
        name: 'points',
        query: to.query,
        hash: to.hash
      }
    }
  })
  routes.push({
    path: '/settings',
    name: 'settings-redirect',
    redirect: (to) => {
      return {
        name: 'points',
        query: { [SettingsQuery.SHOW_SETTINGS]: 'true' },
        hash: to.hash
      }
    }
  })

  return routes
}

export function buildRouter(): Router {
  const globalStore = useGlobalStore()
  const authStore = useAuthorizationStore()
  const userStore = useUserStore()
  const localizationStore = useLocalizationStore()
  const pointsStore = usePointsStore()

  const router = createRouter({
    history: createWebHistory(),
    routes: getRoutesList(),
    scrollBehavior
  })
  let isFirstNavigation = true
  let isCheckedAuthorization = false

  router.beforeEach(async (to, from) => {
    if (!authStore.isCheckedAuthorization) {
      await Promise.all([userStore.getUserInfo(true), localizationStore.getLanguages()])

      if (authStore.isAuthorized) {
        await pointsStore.getPoints()
      }
      isCheckedAuthorization = true
    }

    if ((isFirstNavigation || isCheckedAuthorization) && to.name !== 'set-password') {
      if (isFirstNavigation) isFirstNavigation = false
      if (isCheckedAuthorization) isCheckedAuthorization = false

      const query = { ...to.query }
      const transactionId = query[TransactionsDetailsQuery.TRANSACTION_ID]

      if (query[TransactionFilters.POINT]) {
        query[TransactionsDetailsQuery.POINT_ID] = query[TransactionFilters.POINT]
        delete query[TransactionFilters.POINT]
      }

      if (transactionId) {
        return { name: 'points', query: { ...query, search: transactionId } }
      }

      if (to.name) {
        return { name: to.name, query }
      }
    }

    const authRoutes = [
      'login',
      'password-set-email',
      'password-check-email',
      'set-password',
      'set-password-success'
    ]

    const specialRoutes = [
      'dpkymeuwpmvb'
    ]

    if (!authStore.isAuthorized) {
      if (from.name && from.name !== 'login' && !authRoutes.includes(to.name as string)) {
        return { name: 'login' }
      }
      if (!authRoutes.includes(to.name as string) && !specialRoutes.includes(to.name as string)) {
        return { name: 'login', query: to.query }
      }
    }

    if (authStore.isAuthorized && authRoutes.includes(to.name as string)) {
      return { name: NavigationRoutesNames.POINTS, query: to.query }
    }

    if (to.name === NavigationRoutesNames.ANALYTICS && (!pointsStore.pointList.length || !userStore.isAvailableViewAnalytics)) {
      return { name: NavigationRoutesNames.POINTS, query: to.query }
    }

    if (to.name === NavigationRoutesNames.FINANCE && (!pointsStore.pointList.length || !userStore.isAvailableViewFinance)) {
      return { name: NavigationRoutesNames.POINTS, query: to.query }
    }

    if (to.name === 'home') {
      return { name: NavigationRoutesNames.POINTS, query: to.query }
    }

    return true
  })

  let resizeTimeout: number | undefined

  window.addEventListener('resize', () => {
    globalStore.setAppWidth(window.innerWidth)

    window.clearTimeout(resizeTimeout)
    resizeTimeout = window.setTimeout(() => {
      window.location.reload()
    }, 250)
  })

  return router
}
